import {type PreloadedQuery, graphql, useFragment} from 'react-relay'
import type {ProjectItemSectionFieldsQuery} from './__generated__/ProjectItemSectionFieldsQuery.graphql'
import {useMemo} from 'react'
import type {ProjectItemSectionFields$key} from './__generated__/ProjectItemSectionFields.graphql'
import {SingleSelectField} from '../projects/SingleSelectField'
import {TextField} from '../projects/TextField'
import {NumberField} from '../projects/NumberField'
import {IterationField} from '../projects/IterationField'
import {DateField} from '../projects/DateField'

export type ProjectItemSectionFieldsProps = {
  onIssueUpdate?: () => void
  projectItemId: string
  projectItemsRef: PreloadedQuery<ProjectItemSectionFieldsQuery>
}

type ProjectItemSectionFieldsPropsInternal = {
  projectItem: ProjectItemSectionFields$key
  onIssueUpdate?: () => void
}

export const ProjectItemSectionFieldsGraphqlQuery = graphql`
  query ProjectItemSectionFieldsQuery($owner: String!, $repo: String!, $number: Int!, $allowedOwner: String) {
    repository(owner: $owner, name: $repo) {
      issueOrPullRequest(number: $number) {
        ... on Issue {
          id
          viewerCanUpdateNext
          projectItemsNext(first: 10, allowedOwner: $allowedOwner) @connection(key: "ProjectSection_projectItemsNext") {
            edges {
              node {
                id
                ...ProjectItemSection
              }
            }
          }
        }
        ... on PullRequest {
          id
          viewerCanUpdate
          projectItemsNext(first: 10, allowedOwner: $allowedOwner) @connection(key: "ProjectSection_projectItemsNext") {
            edges {
              node {
                id
                ...ProjectItemSection
              }
            }
          }
        }
      }
    }
  }
`

export function ProjectItemSectionFieldsInternal({projectItem, onIssueUpdate}: ProjectItemSectionFieldsPropsInternal) {
  const projectItemFields = useFragment(
    graphql`
      fragment ProjectItemSectionFields on ProjectV2Item {
        id
        isArchived
        project {
          id
          title
          url
          viewerCanUpdate
          fields(first: 50, orderBy: {field: POSITION, direction: ASC}) {
            edges {
              node {
                ... on ProjectV2Field {
                  id
                  dataType
                  ...TextFieldConfigFragment
                  ...DateFieldConfigFragment
                  ...NumberFieldConfigFragment
                }
                ... on ProjectV2IterationField {
                  id
                  dataType
                  ...IterationFieldConfigFragment
                }
                ... on ProjectV2SingleSelectField {
                  id
                  dataType
                  ...SingleSelectFieldConfigFragment
                }
              }
            }
          }
        }
        fieldValues(first: 50, orderBy: {field: POSITION, direction: ASC}) {
          edges {
            ... on ProjectV2ItemFieldValueEdge {
              node {
                __typename
                ... on ProjectV2ItemFieldTextValue {
                  id
                  field {
                    ... on ProjectV2Field {
                      id
                    }
                  }
                }
                ...TextFieldFragment
                ... on ProjectV2ItemFieldNumberValue {
                  id
                  field {
                    ... on ProjectV2Field {
                      id
                    }
                  }
                }
                ...NumberFieldFragment
                ... on ProjectV2ItemFieldDateValue {
                  id
                  field {
                    ... on ProjectV2Field {
                      id
                    }
                  }
                }
                ...DateFieldFragment
                ... on ProjectV2ItemFieldSingleSelectValue {
                  id
                  field {
                    ... on ProjectV2SingleSelectField {
                      id
                    }
                  }
                }
                ...SingleSelectFieldFragment
                ... on ProjectV2ItemFieldIterationValue {
                  id
                  field {
                    ... on ProjectV2IterationField {
                      id
                    }
                  }
                }
                ...IterationFieldFragment
              }
            }
          }
        }
      }
    `,
    projectItem,
  )

  const fields = useMemo(
    () =>
      (projectItemFields.project.fields?.edges || []).map(edge => {
        if (!edge?.node) {
          return
        }

        const field = edge.node
        const dataType = field.dataType
        const fieldId = field.id
        const value =
          (projectItemFields.fieldValues?.edges || [])
            .flatMap(e => (e?.node ? [e.node] : []))
            .find(e => e?.field?.id === fieldId) || null

        const props = {
          key: fieldId,
          viewerCanUpdate: !projectItemFields.isArchived && projectItemFields.project.viewerCanUpdate,
          itemId: projectItemFields.id,
          projectId: projectItemFields.project.id,
          field,
          value,
          onIssueUpdate,
        }

        switch (dataType) {
          case 'SINGLE_SELECT':
            return <SingleSelectField {...props} />
          case 'TEXT':
            return <TextField {...props} />
          case 'DATE':
            return <DateField {...props} />
          case 'NUMBER':
            return <NumberField {...props} />
          case 'ITERATION':
            return <IterationField {...props} />
        }
      }),
    [
      onIssueUpdate,
      projectItemFields.fieldValues?.edges,
      projectItemFields.id,
      projectItemFields.isArchived,
      projectItemFields.project.fields?.edges,
      projectItemFields.project.id,
      projectItemFields.project.viewerCanUpdate,
    ],
  )

  return <>{fields}</>
}

try{ ProjectItemSectionFieldsInternal.displayName ||= 'ProjectItemSectionFieldsInternal' } catch {}